import { Component, OnInit, ViewChild } from "@angular/core";
import {
  MatTableDataSource,
  MatPaginator,
  PageEvent,
} from "@angular/material";
import { FormControl } from "@angular/forms";
import { AppService } from "src/_services/app.service";
import { formatIndianNumber } from "src/_helpers/utils";
import {
  ARBITRAGE_DECISION_FAILURE_REASON_OPTIONS,
  ARBITRAGE_DECISION_NO_MARGIN_CASE_OPTIONS,
  ARBITRAGE_COMMON_MARKETS,
  ARBITRAGE_MARKET_NAMES_BY_SPORT,
} from "src/_constants/arbitrage-decisions";
import { Router, ActivatedRoute } from "@angular/router";
import * as moment from "moment";

interface DecisionBet {
  providerId: string;
  sportId: string;
  eventId: string;
  marketId: string;
  marketName: string;
  betType: string;
  runnerId: string;
  runnerName: string;
  odd: number;
  stake: number;
  runnerRisk?: number;
}

interface BFPreviousDecision {
  runnerId: string;
  runnerName: string;
  odd: number;
  stake: number;
  betType: string;
  margin: number;
  profit: number;
}

interface ArbitrageDecision {
  id?: string;
  bfBet: DecisionBet;
  srBet: DecisionBet;
  bfRetry?: boolean;
  bfPrevious?: BFPreviousDecision;
  outcome: string;
  failureReason?: string;
  failureDetails?: string;
  margin?: number;
  bfVolume?: number;
  profit?: number;
  marketProfit?: number;
  marginAmount?: number;
  noMarginCase?: string;
  createdAt: number;
  updatedAt?: number;
}

@Component({
  selector: "app-arbitrage-decisions-detail",
  templateUrl: "./arbitrage-decisions-detail.component.html",
  styleUrls: ["./arbitrage-decisions-detail.component.scss"],
})
export class ArbitrageDecisionsDetailComponent implements OnInit {
  outcomeOptions: any[] = [
    { item_id: "SUCCESS", item_text: "SUCCESS" },
    { item_id: "FAILED", item_text: "FAILED" },
  ];
  failureReasonOptions = ARBITRAGE_DECISION_FAILURE_REASON_OPTIONS;
  noMarginCaseOptions = ARBITRAGE_DECISION_NO_MARGIN_CASE_OPTIONS;
  marketOptions: any[] = [];
  outcomeDropdownSettings: any = {};
  failureReasonDropdownSettings: any = {};
  noMarginCaseDropdownSettings: any = {};
  marketDropdownSettings: any = {};
  selectedOutcomeDropdown = new FormControl([]);
  selectedFailureReasonDropdown = new FormControl([]);
  selectedNoMarginCaseDropdown = new FormControl([]);
  selectedMarketDropdown = new FormControl([]);
  selectedOutcome: string = "";
  selectedFailureReasons: string[] = [];
  selectedNoMarginCases: string[] = [];
  selectedMarketNames: string[] = [];
  decisionsList = new MatTableDataSource<ArbitrageDecision>([]);
  displayedColumns: string[] = [
    "createdAt",
    "bfMarketName",
    "margin",
    "noMarginCase",
    "outcome",
    "bfRetry",
    "bfVolume",
    "profit",
    "marketProfit",
    "failureReason",
    "failureDetails",
  ];
  loading: boolean = false;
  expandedElement: ArbitrageDecision | null = null;
  selectedBfPrevious: BFPreviousDecision | null = null;
  eventId: string = "";
  eventName: string = "";
  sportName: string = "";
  sportId: string = "";
  startTime: number = 0;
  endTime: number = 0;

  // Pagination
  @ViewChild(MatPaginator) paginator: MatPaginator;
  page = 1;
  pageSize = 25;
  orgPageSize = 25;
  length = 0;
  pageEvent: PageEvent;

  constructor(
    private appService: AppService,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) { }

  ngOnInit() {
    this.outcomeDropdownSettings = {
      singleSelection: true,
      idField: "item_id",
      textField: "item_text",
      selectAllText: "Select All",
      unSelectAllText: "UnSelect All",
      itemsShowLimit: 1,
      allowSearchFilter: true,
    };
    this.failureReasonDropdownSettings = {
      singleSelection: false,
      idField: "item_id",
      textField: "item_text",
      selectAllText: "Select All",
      unSelectAllText: "UnSelect All",
      itemsShowLimit: 1,
      allowSearchFilter: true,
    };
    this.noMarginCaseDropdownSettings = {
      singleSelection: false,
      idField: "item_id",
      textField: "item_text",
      selectAllText: "Select All",
      unSelectAllText: "UnSelect All",
      itemsShowLimit: 1,
      allowSearchFilter: true,
    };
    this.marketDropdownSettings = {
      singleSelection: false,
      idField: "item_id",
      textField: "item_text",
      selectAllText: "Select All",
      unSelectAllText: "UnSelect All",
      itemsShowLimit: 1,
      allowSearchFilter: true,
    };

    this.activatedRoute.queryParams.subscribe((params) => {
      this.eventId = params.eventId || "";
      this.eventName = params.eventName || "";
      this.sportName = params.sportName || "";
      this.sportId = params.sportId || "";
      this.startTime = params.startTime ? parseInt(params.startTime) : 0;
      this.endTime = params.endTime ? parseInt(params.endTime) : 0;

      this.marketOptions = this.buildMarketOptions(this.sportId);
      // Reset any previously selected markets when changing sport/event.
      this.selectedMarketNames = [];
      this.selectedMarketDropdown.setValue([]);

      if (this.eventId && this.sportId && this.startTime && this.endTime) {
        this.getDecisions();
      }
    });
  }

  private buildMarketOptions(sportId: string): any[] {
    const sid = String(sportId || "").trim();
    const list = (ARBITRAGE_MARKET_NAMES_BY_SPORT[sid] || [ARBITRAGE_COMMON_MARKETS.matchOdds]).slice();
    const unique = Array.from(new Set(list.length ? list : [ARBITRAGE_COMMON_MARKETS.matchOdds]));
    return unique.map((name) => ({ item_id: name, item_text: name }));
  }

  getDecisions = () => {
    if (!this.eventId || !this.startTime || !this.endTime || !this.sportId) {
      this.appService.openErrorBar("Missing required parameters");
      return;
    }

    this.loading = true;
    const filters = {
      eventId: this.eventId,
      sportId: this.sportId,
      startTime: this.startTime,
      endTime: this.endTime,
      page: this.page,
      pageSize: this.orgPageSize,
    };
    if (this.selectedOutcome) {
      filters["outcome"] = this.selectedOutcome;
    }
    if (this.selectedFailureReasons.length) {
      filters["failureReasons"] = this.selectedFailureReasons;
    }
    if (this.selectedNoMarginCases.length) {
      filters["noMarginCases"] = this.selectedNoMarginCases;
    }
    if (this.selectedMarketNames.length) {
      filters["marketNames"] = this.selectedMarketNames;
    }

    this.appService.getArbitrageDecisions(filters).subscribe(
      (res) => {
        this.loading = false;
        if (res.status === "RS_OK") {
          const data: ArbitrageDecision[] = res.decisions || res.data || [];
          this.decisionsList = new MatTableDataSource<ArbitrageDecision>(data);
          if (this.page === 1) {
            this.length = res.totalRecords || 0;
          } else {
            if (this.paginator) {
              this.paginator.length = this.length;
            }
          }
        } else if (res.errorDescription === "Invalid Token!") {
          this.appService.openErrorBar(res.errorDescription);
          sessionStorage.clear();
          this.router.navigateByUrl("/login");
        } else {
          this.appService.openErrorBar(
            res.errorDescription || "Failed to fetch decisions"
          );
        }
      },
      (error) => {
        this.loading = false;
        const errorMessage = (error.error && error.error.errorDescription)
          ? error.error.errorDescription
          : "Error fetching decisions";
        this.appService.openErrorBar(errorMessage);
      }
    );
  };

  changePagination(event: PageEvent) {
    this.pageEvent = event;
    this.page = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.orgPageSize = event.pageSize;
    this.getDecisions();
  }

  refreshDecisions() {
    if (this.loading) {
      return;
    }
    this.getDecisions();
  }

  onOutcomeFilterChange(item: any) {
    this.selectedOutcome = item && item.item_id ? item.item_id : "";
    if (this.selectedOutcome === "SUCCESS") {
      this.selectedFailureReasons = [];
      this.selectedFailureReasonDropdown.setValue([]);
    }
  }

  clearOutcomeFilter() {
    this.selectedOutcome = "";
  }

  onFailureReasonFilterChange(item: any) {
    const selected = item && item.item_id ? item.item_id : "";
    if (selected && !this.selectedFailureReasons.includes(selected)) {
      this.selectedFailureReasons.push(selected);
    }
  }

  onFailureReasonFilterDeselect(item: any) {
    const deselected = item && item.item_id ? item.item_id : "";
    this.selectedFailureReasons = this.selectedFailureReasons.filter(
      (reason) => reason !== deselected
    );
  }

  onFailureReasonFilterSelectAll(items: any[]) {
    this.selectedFailureReasons = (items || [])
      .map((item) => item && item.item_id)
      .filter((value) => !!value);
  }

  clearFailureReasonFilter() {
    this.selectedFailureReasons = [];
  }

  onNoMarginCaseFilterChange(item: any) {
    const selected = item && item.item_id ? item.item_id : "";
    if (selected && !this.selectedNoMarginCases.includes(selected)) {
      this.selectedNoMarginCases.push(selected);
    }
  }

  onNoMarginCaseFilterDeselect(item: any) {
    const deselected = item && item.item_id ? item.item_id : "";
    this.selectedNoMarginCases = this.selectedNoMarginCases.filter(
      (value) => value !== deselected
    );
  }

  onNoMarginCaseFilterSelectAll(items: any[]) {
    this.selectedNoMarginCases = (items || [])
      .map((item) => item && item.item_id)
      .filter((value) => !!value);
  }

  clearNoMarginCaseFilter() {
    this.selectedNoMarginCases = [];
  }

  onMarketFilterChange(item: any) {
    const selected = item && item.item_id ? item.item_id : "";
    if (selected && !this.selectedMarketNames.includes(selected)) {
      this.selectedMarketNames.push(selected);
    }
  }

  onMarketFilterDeselect(item: any) {
    const deselected = item && item.item_id ? item.item_id : "";
    this.selectedMarketNames = this.selectedMarketNames.filter((value) => value !== deselected);
  }

  onMarketFilterSelectAll(items: any[]) {
    this.selectedMarketNames = (items || [])
      .map((i) => (i && i.item_id ? i.item_id : ""))
      .filter((v) => !!v);
  }

  clearMarketFilter() {
    this.selectedMarketNames = [];
    this.selectedMarketDropdown.setValue([]);
  }

  applyDecisionFilters() {
    this.page = 1;
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.getDecisions();
  }

  toggleRow(element: ArbitrageDecision) {
    this.expandedElement = this.expandedElement === element ? null : element;
  }

  isExpanded(element: ArbitrageDecision): boolean {
    return this.expandedElement === element;
  }

  formatCreatedAt(createdAt: number): string {
    if (!createdAt) return "";
    // createdAt is in unixmilli, convert to moment and format
    return moment(createdAt).format("DD-MM-YYYY HH:mm:ss.SSS");
  }

  getBetTypeClass(betType: string): string {
    if (betType === "BACK") {
      return "bet-type-back";
    } else if (betType === "LAY") {
      return "bet-type-lay";
    }
    return "";
  }

  formatFixed(value: any, precision: number = 2): string {
    return formatIndianNumber(value, precision);
  }

  openBfPreviousPopup(element: ArbitrageDecision, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    if (element && element.bfRetry && element.bfPrevious) {
      this.selectedBfPrevious = element.bfPrevious;
    }
  }

  closeBfPreviousPopup(event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.selectedBfPrevious = null;
  }

  goBack() {
    this.router.navigate(["/administration/event-arbitrage-decisions"], {
      queryParams: {
        sportId: this.sportId,
        sportName: this.sportName,
        startTime: this.startTime,
        endTime: this.endTime,
      },
    });
  }
}

